import { useState } from 'react';

import {
  NewLocationService,
  NewService,
  Location,
  Service,
  RestaurantService,
} from '../../../interfaces';
import { Button } from '@material-tailwind/react';
import { MdCreate } from 'react-icons/md';
import LocationServiceInputCardsContainer from '../InputComponents/LocationServiceInputCardsContainer';
import { Waypoint } from 'react-waypoint';

/**
 * To get an array of new locationService where all the placeholder IDs are removed
 * @param mapNewLocationByLocationId - Map of key: locationId and value: newLocation
 * @param mapMapNewServiceByServiceIdByLocationId - Map of key: locationId and value: Map of key: serviceId and value: newService
 * @returns arrNewLocationService - Array of newLocationService with the placeholder IDs removed
 */
const getArrNewLocationService = (
  mapNewLocationByLocationId: Map<number, Location>,
  mapMapNewServiceByServiceIdByLocationId: Map<number, Map<number, Service>>
): Array<NewLocationService> => {
  const arrNewLocationService: Array<NewLocationService> = [];
  mapNewLocationByLocationId.forEach((newLocation: Location, locationId: number) => {
    const mapNewServiceByServiceId = mapMapNewServiceByServiceIdByLocationId.get(locationId);
    const arrNewService: Array<NewService> = [];
    mapNewServiceByServiceId?.forEach((newService: Service, serviceId: number) => {
      const newServiceWithoutPlaceholderIds: NewService = { ...newService };
      newServiceWithoutPlaceholderIds.serviceId = undefined;
      newServiceWithoutPlaceholderIds.locationId = undefined;
      newServiceWithoutPlaceholderIds.menuId = undefined;
      newServiceWithoutPlaceholderIds.menu.menuId = undefined;
      arrNewService.push(newServiceWithoutPlaceholderIds);
    });
    const newLocationService: NewLocationService = {
      ...newLocation,
      locationId: undefined,
      arrService: arrNewService,
    };
    arrNewLocationService.push(newLocationService);
  });
  return arrNewLocationService;
};

const AddLocationInputCard = (props: {
  restaurantService: RestaurantService;
  saveChangesToExistingCompanyService: any;
}) => {
  const [mapNewLocationByLocationId, setMapNewLocationByLocationId] = useState(new Map());
  const [mapMapNewServiceByServiceIdByLocationId, setMapMapNewServiceByServiceIdByLocationId] =
    useState(new Map());
  const [isButtonFloating, setIsButtonFloating] = useState<boolean>(false);

  /**
   * Function can add the new location to map, with the locationId as the key, or remove the key-value if the location is to be removed.
   * If the location is to be removed, remove all the new service(s) that belongs to this location.
   * @param idParameters - Object containing locationId of the location
   * @param newLocation - New location object, or undefined if the location is to be removed
   */
  const updateMapNewLocationByLocationId = (
    idParameters: { locationId: number },
    newLocation?: Location
  ) => {
    const { locationId } = idParameters;
    const updatedMapNewLocationByLocationId = new Map(mapNewLocationByLocationId);
    if (newLocation) {
      updatedMapNewLocationByLocationId.set(locationId, newLocation);
    } else {
      updatedMapNewLocationByLocationId.delete(locationId);
      const updatedMapMapNewServiceByServiceIdByLocationId = new Map(
        mapMapNewServiceByServiceIdByLocationId
      );
      updatedMapMapNewServiceByServiceIdByLocationId.delete(locationId);
      setMapMapNewServiceByServiceIdByLocationId(updatedMapMapNewServiceByServiceIdByLocationId);
    }
    setMapNewLocationByLocationId(updatedMapNewLocationByLocationId);
  };

  /**
   * Function can add the new service to map, with the locationId as the first key and serviceId as the second key, or remove the key-value if the service
   * is to be removed
   * @param idParameters - Object containing serviceId and locationId of the service
   * @param newService - New service object, or undefined if the service is to be removed
   */
  const updateMapMapNewServiceByServiceIdByLocationId = (
    idParameters: { serviceId: number; locationId: number },
    newService?: Location
  ) => {
    const { locationId, serviceId } = idParameters;
    const updatedMapMapNewServiceByServiceIdByLocationId = new Map(
      mapMapNewServiceByServiceIdByLocationId
    );
    if (newService) {
      if (updatedMapMapNewServiceByServiceIdByLocationId.get(locationId)) {
        updatedMapMapNewServiceByServiceIdByLocationId.get(locationId).set(serviceId, newService);
      } else {
        updatedMapMapNewServiceByServiceIdByLocationId.set(
          locationId,
          new Map([[serviceId, newService]])
        );
      }
    } else {
      updatedMapMapNewServiceByServiceIdByLocationId.get(locationId)?.delete(serviceId);
    }
    setMapMapNewServiceByServiceIdByLocationId(updatedMapMapNewServiceByServiceIdByLocationId);
  };

  /**
   * Function is run by save changes button, and calls saveChangesToExistingCompanyService function that is
   * passed down from parent CompanyCard component. Function saves newly added locationService (if any) for the
   * restaurant.
   */
  const saveChangesForAddLocationServiceOnClick = async () => {
    const arrNewLocationService = getArrNewLocationService(
      mapNewLocationByLocationId,
      mapMapNewServiceByServiceIdByLocationId
    );
    const isSaved = await props.saveChangesToExistingCompanyService(
      {
        companyId: props.restaurantService.companyId,
        restaurantId: props.restaurantService.restaurantId,
      },
      {
        arrLocationServiceToBeCreated: arrNewLocationService,
      },
      {}
    );
    // Reset changes if the locationService(s) has been added successfully
    if (isSaved) {
      setMapNewLocationByLocationId(new Map());
      setMapMapNewServiceByServiceIdByLocationId(new Map());
    }
  };

  return (
    <div className="flex flex-col">
      <Button
        onClick={saveChangesForAddLocationServiceOnClick}
        size="sm"
        color="green"
        className={`${
          isButtonFloating && 'fixed top-20 z-10'
        } SaveChangesButton bg-blue-600 w-fit self-end flex gap-x-1 pt-2 p-2 font-medium h-fit'}`}
      >
        <MdCreate className="self-center" />
        Save Changes
      </Button>
      <Waypoint
        onEnter={() => setIsButtonFloating(false)}
        onLeave={() => setIsButtonFloating(true)}
      />
      <LocationServiceInputCardsContainer
        arrNewLocation={Array.from(mapNewLocationByLocationId.values())}
        arrLocationService={
          props.restaurantService !== undefined ? props.restaurantService!.arrLocationService : []
        }
        mapMapNewServiceByServiceIdByLocationId={mapMapNewServiceByServiceIdByLocationId}
        companyId={props.restaurantService.companyId}
        restaurantId={props.restaurantService.restaurantId}
        updateMapForNewLocation={updateMapNewLocationByLocationId}
        updateMapMapNewServiceByServiceIdByLocationId={
          updateMapMapNewServiceByServiceIdByLocationId
        }
      />
    </div>
  );
};

export default AddLocationInputCard;
